/**
 * Offline-Warteschlange für Aktionen (Mülltonne rausgestellt, Geräte starten …).
 * Wie die Eintrags-Outbox ohne Browser-Abhängigkeiten und mit injizierbarem
 * Speicher. Unterschiede: Reihenfolge bleibt erhalten (FIFO, z. B. „Start"
 * vor „Verlängern"), und abgelehnte Aktionen werden entfernt und gemeldet –
 * sie sind Information („schon erledigt von …"), keine Korrekturaufgabe.
 */
import { NetworkError } from './outbox.js';

export interface QueuedAction {
  id: string;
  type: string;
  payload: Record<string, unknown>;
  at: number;
  attempts: number;
}

export interface ActionResult {
  id: string;
  status: 'ok' | 'rejected';
  error?: string;
  data?: unknown;
}

export interface ActionStorage {
  load(): Promise<QueuedAction[]>;
  save(items: QueuedAction[]): Promise<void>;
}

export type ActionSender = (actions: Omit<QueuedAction, 'attempts'>[]) => Promise<ActionResult[]>;

export interface ActionReport {
  ok: ActionResult[];
  rejected: (ActionResult & { type: string })[];
  offline: boolean;
  error?: string;
}

export function memoryActionStorage(initial: QueuedAction[] = []): ActionStorage & { data: QueuedAction[] } {
  const s = {
    data: structuredClone(initial),
    async load() {
      return structuredClone(s.data);
    },
    async save(items: QueuedAction[]) {
      s.data = structuredClone(items);
    },
  };
  return s;
}

export class ActionQueue {
  private items: QueuedAction[] | null = null;
  private running: Promise<ActionReport> | null = null;
  private listeners = new Set<() => void>();

  constructor(private storage: ActionStorage, private batchSize = 50) {}

  private async all(): Promise<QueuedAction[]> {
    if (!this.items) this.items = await this.storage.load();
    return this.items;
  }

  private async persist(): Promise<void> {
    await this.storage.save(this.items ?? []);
    for (const l of this.listeners) l();
  }

  onChange(cb: () => void): () => void {
    this.listeners.add(cb);
    return () => this.listeners.delete(cb);
  }

  async list(): Promise<QueuedAction[]> {
    return [...(await this.all())];
  }

  async count(): Promise<number> {
    return (await this.all()).length;
  }

  async enqueue(a: { id: string; type: string; payload: Record<string, unknown>; at: number }): Promise<void> {
    const items = await this.all();
    if (items.some((x) => x.id === a.id)) return; // gleiche UUID nur einmal
    items.push({ ...structuredClone(a), attempts: 0 });
    await this.persist();
  }

  /** Noch nicht gesendete Aktion zurücknehmen (z. B. „Rückgängig" vor dem Upload). */
  async discard(id: string): Promise<boolean> {
    const items = await this.all();
    const before = items.length;
    this.items = items.filter((x) => x.id !== id);
    await this.persist();
    return this.items.length !== before;
  }

  async reset(): Promise<void> {
    this.items = [];
    await this.persist();
  }

  flush(send: ActionSender): Promise<ActionReport> {
    if (!this.running) {
      this.running = this.doFlush(send).finally(() => {
        this.running = null;
      });
    }
    return this.running;
  }

  private async doFlush(send: ActionSender): Promise<ActionReport> {
    const report: ActionReport = { ok: [], rejected: [], offline: false };
    for (;;) {
      const batch = (await this.all()).slice(0, this.batchSize);
      if (!batch.length) return report;
      let results: ActionResult[];
      try {
        results = await send(batch.map(({ attempts: _a, ...rest }) => rest));
      } catch (err) {
        for (const b of batch) b.attempts++;
        await this.persist();
        report.offline = err instanceof NetworkError;
        report.error = (err as Error).message;
        return report;
      }
      const answered = new Set<string>();
      for (const r of results) {
        answered.add(r.id);
        const type = batch.find((b) => b.id === r.id)?.type ?? '';
        if (r.status === 'ok') report.ok.push(r);
        else report.rejected.push({ ...r, type });
      }
      this.items = (await this.all()).filter((x) => !answered.has(x.id));
      await this.persist();
      if (!answered.size) return report;
    }
  }
}
